/**
 * Agents store for managing client-side agent UI state
 */

import { create } from 'zustand';
import { Agent } from '../api/base/types';

/**
 * Interface for the agents store
 */
interface AgentsStore {
  // Search state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  
  // Agent detail modal state
  selectedAgent: Agent | null;
  isDetailModalOpen: boolean;
  openDetailModal: (agent: Agent) => void;
  closeDetailModal: () => void;
  
  // UI state
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  
  // Error state
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Utility methods
  reset: () => void;

  // Preselected agent for chat flow
  preselectedAgentId: string | null;
  setPreselectedAgentId: (agentId: string | null) => void;
  clearPreselectedAgent: () => void;

  // Preselected prompt text for chat input
  preselectedPromptText: string | null;
  setPreselectedPromptText: (text: string | null) => void;
  clearPreselectedPromptText: () => void;
}

/**
 * Initial state for the agents store
 */
const initialState = {
  searchQuery: '',
  selectedAgent: null,
  isDetailModalOpen: false,
  isLoading: false,
  error: null,
  preselectedAgentId: null,
  preselectedPromptText: null,
};

/**
 * Agents store implementation
 */
export const useAgentsStore = create<AgentsStore>((set, get) => ({
  // Initial state
  ...initialState,
  
  // Search actions
  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
    // Clear error when user starts searching
    if (get().error) {
      set({ error: null });
    }
  },
  
  clearSearch: () => set({ searchQuery: '' }),
  
  // Agent detail modal actions
  openDetailModal: (agent: Agent) => set({ 
    selectedAgent: agent, 
    isDetailModalOpen: true,
    error: null, // Clear any existing errors
  }),
  
  closeDetailModal: () => set({ 
    selectedAgent: null, 
    isDetailModalOpen: false 
  }),
  
  // UI state actions
  setLoading: (loading: boolean) => set({ isLoading: loading }),
  
  // Error state actions
  setError: (error: string | null) => set({ error }),
  
  clearError: () => set({ error: null }),
  
  // Utility actions
  reset: () => set(initialState),

  // Preselected agent plumbing for Start New Chat
  setPreselectedAgentId: (agentId: string | null) => set({ preselectedAgentId: agentId }),
  clearPreselectedAgent: () => set({ preselectedAgentId: null }),

  // Preselected prompt text plumbing for Start New Chat with example prompt
  setPreselectedPromptText: (text: string | null) => set({ preselectedPromptText: text }),
  clearPreselectedPromptText: () => set({ preselectedPromptText: null }),
}));

/**
 * Selectors for commonly used derived state
 */
export const agentsStoreSelectors = {
  // Get current search state
  getSearchState: (state: AgentsStore) => ({
    query: state.searchQuery,
    hasQuery: state.searchQuery.trim().length > 0,
  }),
  
  // Get modal state
  getModalState: (state: AgentsStore) => ({
    isOpen: state.isDetailModalOpen,
    agent: state.selectedAgent,
  }),
  
  // Get UI state
  getUIState: (state: AgentsStore) => ({
    isLoading: state.isLoading,
    error: state.error,
    hasError: !!state.error,
  }),
};

/**
 * Hook for accessing search state
 */
export const useAgentsSearch = () => {
  const { searchQuery, setSearchQuery, clearSearch } = useAgentsStore();
  const hasQuery = searchQuery.trim().length > 0;
  
  return {
    query: searchQuery,
    hasQuery,
    setQuery: setSearchQuery,
    clearQuery: clearSearch,
  };
};

/**
 * Hook for accessing modal state
 */
export const useAgentsModal = () => {
  const { selectedAgent, isDetailModalOpen, openDetailModal, closeDetailModal } = useAgentsStore();
  
  return {
    agent: selectedAgent,
    isOpen: isDetailModalOpen,
    open: openDetailModal,
    close: closeDetailModal,
  };
};

/**
 * Hook for accessing UI state
 */
export const useAgentsUI = () => {
  const { isLoading, error, setLoading, setError, clearError } = useAgentsStore();
  
  return {
    isLoading,
    error,
    hasError: !!error,
    setLoading,
    setError,
    clearError,
  };
};
